import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import ArrowRight from 'components/Icons/ArrowRight'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import { usePermissionReason } from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import React from 'react'
import { useDispatch } from 'react-redux'
import { push } from 'react-router-redux'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { useAbility } from 'utilities/ability'
import SectionShell from './SectionShell'
import usePromoCtaTarget from './usePromoCtaTarget'

const t = buildTranslate('recovery_ai.promo.cta')
const tPermission = buildTranslate('nav.permission_tooltip')

// Figma 129:1828 (closing CTA band): centered Header 2 headline over the CTA row,
// 40/86 padding on a grey05 field, 24px stack gap.
const SECTION_PADDING_VERTICAL = '40px'
const SECTION_PADDING_HORIZONTAL = '86px'
const STACK_GAP = '24px'
// The Figma button pairs its label with a 20px trailing arrow.
const ARROW_SIZE = 20

const CtaSection = () => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  // Same target derivation as the Hero CTA: a configured-but-off merchant is sent
  // to the activation CTA rather than back through the wizard.
  const ctaTarget = usePromoCtaTarget()
  // Mirrors the Hero CTA: an RBAC view-only operator reaches the promo page but
  // can't start activation, so the CTA is disabled behind the shared
  // PermissionTooltip (reason resolves only when the RBAC tooltip flags are on).
  const ability = useAbility()
  const cannotManage = ability.cannot('manage', 'MerchantSettings')
  const activateDisabledReason = usePermissionReason(
    'MerchantSettings',
    cannotManage,
    tPermission('view_and_edit_suffix')
  )

  return (
    <SectionShell
      backgroundColor={merchantTheme.colors.grey05}
      padding={`${SECTION_PADDING_VERTICAL} ${SECTION_PADDING_HORIZONTAL}`}
    >
      <div
        className={css({
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          gap: STACK_GAP,
        })}
      >
        <Text.Header2
          tag="h2"
          className={css({
            // No max-width: the headline spans the full CTA band so it reads as
            // a single line on a wide screen instead of orphaning the last word
            // ("...when it works") onto its own line at the Figma's 628px cap.
            // It still wraps gracefully on narrower viewports.
            textAlign: 'center',
          })}
        >
          {t('title')}
        </Text.Header2>
        {/* DELIBERATE DEVIATION from Figma 129:1831, which shows two CTAs ("Pilot at
          select locations" / "Turn on for all N locations"). Both would need a real
          location count and two distinct activation flows; until those exist this
          band ships ONE primary CTA into the onboarding wizard — the only
          interactive element on the promo page. */}
        <PermissionTooltip
          disabled={cannotManage}
          reason={activateDisabledReason}
          id="recovery-ai-promo-cta-activate-perm"
          placement="top"
          inline
        >
          <Button
            kind="primary"
            disabled={cannotManage}
            onClick={() => dispatch(push(ctaTarget))}
            endEnhancer={
              <ArrowRight
                className={css({
                  height: `${ARROW_SIZE}px`,
                  width: `${ARROW_SIZE}px`,
                })}
                fillColor={merchantTheme.colors.white}
              />
            }
          >
            {t('activate')}
          </Button>
        </PermissionTooltip>
      </div>
    </SectionShell>
  )
}

export default CtaSection
